import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PaymentServices from "../../services/PaymentServices";
import PaymentPageForm from "./card";
import DashboardLayout from "../common/DashboardLayout";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const PaymentPage = () => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = Number(localStorage.getItem("userrole") || 0);
  const isSchool = userRole === 1;
  const isTeacher = userRole === 3;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        setIsLoading(true);
        
        // First check localStorage for payment details from login
        const storedNeedsPayment = localStorage.getItem("needsPayment");
        const storedPaymentDetails = localStorage.getItem("paymentDetails");
        
        if (storedNeedsPayment === "true" && storedPaymentDetails) {
          try {
            const parsedDetails = JSON.parse(storedPaymentDetails);
            console.log("PaymentPage: Using localStorage payment details:", parsedDetails);
            // Validate that we have a valid amount
            const hasValidAmount = (parsedDetails.amount && parsedDetails.amount > 0) || 
                                   (parsedDetails.totalAmount && parsedDetails.totalAmount > 0);
            if (hasValidAmount) {
              setPaymentStatus({
                needsPayment: true,
                amount: parsedDetails.amount || parsedDetails.totalAmount,
                totalAmount: parsedDetails.totalAmount || parsedDetails.amount,
                planType: parsedDetails.planType || "annual",
                isFirstPayment: parsedDetails.installmentsPaid === 0 || parsedDetails.installmentsPaid === undefined,
                payment: parsedDetails,
              });
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing stored payment details:", e);
          }
        }
        
        // Call payment status API to get latest status
        const response = await PaymentServices.checkPaymentStatus();
        
        // If response is null, endpoint doesn't exist - use localStorage fallback
        if (response === null) {
          if (storedNeedsPayment === "true" && storedPaymentDetails) {
            try {
              const parsedDetails = JSON.parse(storedPaymentDetails);
              const hasValidAmount = (parsedDetails.amount && parsedDetails.amount > 0) || 
                                     (parsedDetails.totalAmount && parsedDetails.totalAmount > 0);
              if (hasValidAmount) {
                setPaymentStatus({
                  needsPayment: true,
                  amount: parsedDetails.amount || parsedDetails.totalAmount,
                  totalAmount: parsedDetails.totalAmount || parsedDetails.amount,
                  planType: parsedDetails.planType || "annual",
                  isFirstPayment: parsedDetails.installmentsPaid === 0 || parsedDetails.installmentsPaid === undefined,
                  payment: parsedDetails,
                });
              } else {
                setPaymentStatus({ needsPayment: false, amount: null });
              }
            } catch (e) {
              console.error("Error parsing stored payment details:", e);
              setPaymentStatus({ needsPayment: false, amount: null });
            }
          } else {
            setPaymentStatus({ needsPayment: false, amount: null });
          }
        } else if (response && response.needsPayment) {
          // Use payment details from API response
          const totalAmt = response.totalAmount || response.amount || 0;
          const details = response.payment || response.paymentDetails || {
            amount: response.amount,
            totalAmount: totalAmt,
            planType: response.planType || "annual",
            installmentsTotal: response.installmentsTotal || 0,
            // ONLY use backend-provided installmentAmount - NO client-side calculations
            installmentAmount: response.installmentAmount || 0,
            installmentsPaid: response.installmentsPaid || 0,
          };
          setPaymentStatus({
            needsPayment: true,
            amount: details.amount || details.totalAmount,
            totalAmount: details.totalAmount || details.amount,
            planType: details.planType || "annual",
            isFirstPayment: response.isFirstPayment !== false,
            payment: details,
          });
        } else {
          setPaymentStatus({ needsPayment: false, amount: null });
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        // On error, check localStorage as fallback
        const storedNeedsPayment = localStorage.getItem("needsPayment");
        const storedPaymentDetails = localStorage.getItem("paymentDetails");
        if (storedNeedsPayment === "true" && storedPaymentDetails) {
          try {
            const parsedDetails = JSON.parse(storedPaymentDetails);
            const hasValidAmount = (parsedDetails.amount && parsedDetails.amount > 0) || 
                                   (parsedDetails.totalAmount && parsedDetails.totalAmount > 0);
            if (hasValidAmount) {
              setPaymentStatus({
                needsPayment: true,
                amount: parsedDetails.amount || parsedDetails.totalAmount,
                totalAmount: parsedDetails.totalAmount || parsedDetails.amount,
                planType: parsedDetails.planType || "annual",
                isFirstPayment: parsedDetails.installmentsPaid === 0 || parsedDetails.installmentsPaid === undefined,
                payment: parsedDetails,
              });
            } else {
              setPaymentStatus({ needsPayment: false, amount: null });
            }
          } catch (e) {
            console.error("Error parsing stored payment details:", e);
            setPaymentStatus({ needsPayment: false, amount: null });
          }
        } else {
          setPaymentStatus({ needsPayment: false, amount: null });
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentStatus();
  }, [navigate, isSchool, isTeacher]);

  const handlePaymentSuccess = async (response, planType) => {
    // Clear payment flags after successful payment
    localStorage.removeItem("needsPayment");
    localStorage.removeItem("paymentDetails");

    // Refresh payment status after successful payment
    try {
      const updatedStatus = await PaymentServices.checkPaymentStatus();
      setPaymentStatus(updatedStatus);
    } catch (error) {
      console.error("Error refreshing payment status:", error);
    }

    // Navigate to appropriate dashboard
    if (isSchool) {
      navigate("/main-dashboard");
    } else if (isTeacher) {
      navigate("/main-dashboard");
    }
  };

  const userobj = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (isLoading) {
    return (
      <DashboardLayout 
        role={isSchool ? 1 : 3} 
        userType={userobj?.type}
        activeMenu="Payment"
        layoutType="topnav"
      >
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Extract amount from paymentStatus, prioritizing amount over totalAmount
  const amount = paymentStatus?.amount || paymentStatus?.payment?.amount || paymentStatus?.payment?.totalAmount || paymentStatus?.totalAmount || 0;
  const isFirstPayment = paymentStatus?.isFirstPayment !== false;
  const needsPayment = paymentStatus?.needsPayment === true;
  
  // Don't show payment form if amount is 0 or invalid
  if (needsPayment && amount === 0) {
    console.warn("PaymentPage: Payment required but amount is 0", paymentStatus);
  }

  return (
    <DashboardLayout 
      role={isSchool ? 1 : 3} 
      userType={userobj?.type}
      activeMenu="Payment"
      layoutType="topnav"
    >
              <div className="card">
                <div className="card-body">
                  {needsPayment ? (
                    <>
                      <h4 className="mb-3">Complete Your Payment</h4>
                      <PaymentPageForm
                        amount={paymentStatus?.payment?.amount || paymentStatus?.amount || amount}
                        totalAmount={
                          // IMPORTANT: Use baseAmount if available (original amount before discount)
                          // Otherwise use totalAmount (which should be the base amount, not discounted)
                          paymentStatus?.payment?.baseAmount || 
                          paymentStatus?.baseAmount || 
                          paymentStatus?.payment?.totalAmount || 
                          paymentStatus?.totalAmount || 
                          amount
                        }
                        installmentAmount={
                          // ONLY use backend-provided installmentAmount - NO client-side calculations
                          paymentStatus?.payment?.installmentAmount || 
                          paymentStatus?.installmentAmount || 
                          0
                        }
                        onSuccess={handlePaymentSuccess}
                        pageType={isFirstPayment ? "first_payment" : "installment"}
                      />
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <div className="mb-4">
                        <i className="bx bx-check-circle text-success" style={{ fontSize: "4rem" }}></i>
                      </div>
                      <h4 className="mb-3">No Payment Required</h4>
                      <p className="text-muted mb-4">
                        Your account is up to date. No payment is required at this time.
                      </p>
                      <div className="d-flex gap-2 justify-content-center">
                        <Link
                          to={isSchool ? "/school-order-history" : "/teacher-order-history"}
                          className="btn btn-primary"
                        >
                          <i className="bx bx-history me-1"></i>
                          View Payment History
                        </Link>
                        <Link
                          to={isSchool ? "/main-dashboard" : "/main-dashboard"}
                          className="btn btn-outline-secondary"
                        >
                          Back to Dashboard
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
    </DashboardLayout>
  );
};

export default PaymentPage;

